import type { Persistor } from 'redux-persist';

import { reportsApi } from '../features/reports/reportsApi';
import { userAccountsApi } from '../features/userAccounts/usersApi';
import { retentionGovernanceApi } from '../features/retentionGovernance/retentionGovernanceApi';
import { createAppPersistor, createAppStore, type AppStore } from '../store';
import type { AppDependencies } from './appDependencies';

export interface AppRuntime {
  store: AppStore;
  persistor: Persistor;
  resetSessionState: () => Promise<void>;
}

export interface CreateAppRuntimeOptions {
  createStore?: (dependencies: AppDependencies) => AppStore;
  createPersistor?: (store: AppStore) => Persistor;
}

export const createAppRuntime = (
  dependencies: AppDependencies,
  options: CreateAppRuntimeOptions = {}
): AppRuntime => {
  const store = (options.createStore ?? createAppStore)(dependencies);
  const persistor = (options.createPersistor ?? createAppPersistor)(store);

  return {
    store,
    persistor,
    resetSessionState: async () => {
      try {
        await persistor.flush();
      } catch {
        // Non-fatal: continue cleanup even if persistence flush fails.
      }

      dependencies.reportsInvalidation.cleanupAllListeners();
      store.dispatch(reportsApi.util.resetApiState());
      store.dispatch(userAccountsApi.util.resetApiState());
      store.dispatch(retentionGovernanceApi.util.resetApiState());

      try {
        await persistor.purge();
      } catch {
        // Non-fatal: continue cleanup even if persistence purge fails.
      }

      store.dispatch({ type: 'LOGOUT_RESET' });
    },
  };
};
